import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import { media } from 'styles/media';
import { useAdvisor } from 'app/Provider/AdvisorProvider';
import Lottie from 'lottie-react';
import loader from 'app/components/AdvisorForm/white loading.json';

import { useSelector } from 'react-redux';
import { selectUpdateAdvisor } from '../SequentialSteps/slice/selectors';
interface Props {
  onSubmit(values: any): void;
}

export const KycDocumentUpload = memo(
  forwardRef((props: Props, ref) => {
    const [gstNumber, setGstNumber] = useState('');
    const [panFile, setPanFile] = useState<File | null>(null);
    const [addressProofType, setAddressProofType] = useState('');
    const [addressProofFile, setAddressProofFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [identityFileName, setIdentityFileName] = useState('');
    const [addressFileName, setAddressFileName] = useState('');
    const [identityFileSelected, setIdentityFileSelected] = useState(false);
    const [addressFileSelected, setAddressFileSelected] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [hasGst, setHasGst] = useState(false);
    const { successResponse, failureResponse } =
      useSelector(selectUpdateAdvisor);
    const identityFileInputRef = useRef<HTMLInputElement | null>(null);
    const addressFileInputRef = useRef<HTMLInputElement | null>(null);
    const {
      advisorData,
      setTrackSubmission,
      setGst,
      nextStep,
      setProofType,
      setFileOne,
      setFileTwo,
      setFileOneName,
      setFileTwoName,
    } = useAdvisor();
    const AdvisorOnboardbaseUrl = `${process.env.REACT_APP_CDN_URL}/advisor/advisorVerify`;
    const Add = `${process.env.REACT_APP_CDN_URL}/advisor/advisorDashboard/uploadBtn.svg`;
    const success = `${AdvisorOnboardbaseUrl}/uploaded.svg`;
    const Removefile = `${AdvisorOnboardbaseUrl}/removeFile.svg`;
    const solar = `${AdvisorOnboardbaseUrl}/upload.svg`;
    // File size constraints
    const maxFileSize = 200 * 1024; // 200 KB in bytes
    const minFileSize = 10 * 1024; // 10 KB in bytes

    // Expose the submitForm function to the parent
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit();
      },
    }));
    useEffect(() => {
      if (successResponse) {
        setShowLoader(false);

        nextStep();
      }
    }, [successResponse, nextStep]);

    useEffect(() => {
      if (failureResponse) {
        setShowLoader(false);
      }
    }, [failureResponse]);

    useEffect(() => {
      if (advisorData.individualdata.proof_type !== '') {
        setAddressProofType(advisorData.individualdata.proof_type);
      }
      if (advisorData.individualdata.gst_number !== '') {
        setGstNumber(advisorData.individualdata.gst_number);
      }
      if (advisorData?.individualdata.panFile) {
        setPanFile(advisorData?.individualdata.panFile);
        setIdentityFileName(advisorData?.individualdata.panFileName);
        setIdentityFileSelected(true);
      }
      if (advisorData?.individualdata.proofFile) {
        setAddressProofFile(advisorData?.individualdata.proofFile);
        setAddressFileName(advisorData?.individualdata.proofFileName);
        setAddressFileSelected(true);
      }
    }, [
      advisorData.individualdata.panFile,
      advisorData.individualdata.proofFile,
      advisorData.individualdata.panFileName,
      advisorData.individualdata.proofFileName,
      advisorData.individualdata.gst_number,
      advisorData.individualdata.proof_type,
    ]);
    // Validate GST number
    const validateGstNumber = (value: string) => {
      if (!hasGst) return '';
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!value) return 'GST number is required';
      if (!gstRegex.test(value)) return 'Invalid GST number format';
      return '';
    };

    // Validate file
    const validateFile = (file: File | null, fieldName: string) => {
      if (!file) return `${fieldName} is required`;
      if (file.size < minFileSize || file.size > maxFileSize)
        return 'File size must be between 10 KB and 200 KB';
      if (
        !['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'].includes(
          file.type,
        )
      )
        return 'Unsupported file format. Only PNG, JPG, JPEG, and PDF are allowed.';
      return '';
    };

    // Handle PAN file change
    const handlePanFileChange = (file: File | null) => {
      if (file) {
        const error = validateFile(file, 'PAN file');
        setErrors(prev => ({ ...prev, PAN_file: error }));
        setPanFile(file);
        setIdentityFileName(file.name);
        setIdentityFileSelected(true);
        setFileOne(file);
        setFileOneName(file.name);
      } else {
        setErrors(prev => ({ ...prev, PAN_file: '' }));
        setPanFile(null);
        setIdentityFileName('');
        setIdentityFileSelected(false);
        setFileOne(null);
        setFileOneName('');
      }
    };

    // Handle Address Proof file change
    const handleAddressProofFileChange = (file: File | null) => {
      if (file) {
        const error = validateFile(file, 'Address proof file');
        setErrors(prev => ({ ...prev, address_proof_file: error }));
        setAddressProofFile(file);
        setAddressFileName(file.name);
        setAddressFileSelected(true);
        setFileTwo(file);
        setFileTwoName(file.name);
      } else {
        setErrors(prev => ({ ...prev, address_proof_file: '' }));
        setAddressProofFile(null);
        setAddressFileName('');
        setAddressFileSelected(false);
        setFileTwo(null);
        setFileTwoName('');
      }
    };

    const handleGstNumberChange = (value: string) => {
      setGst(value);
      // Only validate if GST checkbox is checked
      if (hasGst) {
        const error = validateGstNumber(value);
        setErrors(prev => ({ ...prev, gst_number: error }));
      } else {
        // Clear GST validation error when GST toggle is off
        setErrors(prev => ({ ...prev, gst_number: '' }));
      }
    };

    // Add handler for address proof type selection to clear validation
    const handleAddressProofTypeChange = (type: string) => {
      setProofType(type);
      setAddressProofType(type);
      setErrors(prev => ({ ...prev, address_proof_type: '' }));
    };

    // Handle form submission
    const handleSubmit = () => {
      // Validate all fields and create updated errors object
      const updatedErrors: Record<string, string> = {};

      // Only validate GST if the checkbox is checked
      if (hasGst) {
        const gstError = validateGstNumber(gstNumber);
        if (gstError) updatedErrors.gst_number = gstError;
      }

      // Validate PAN file
      if (!panFile) {
        updatedErrors.PAN_file = 'PAN file is required';
      } else {
        const panFileError = validateFile(panFile, 'PAN file');
        if (panFileError) updatedErrors.PAN_file = panFileError;
      }

      // Validate address proof type
      if (!addressProofType) {
        updatedErrors.address_proof_type =
          'Please select a proof of address type';
      }

      // Validate address proof file
      if (!addressProofFile) {
        updatedErrors.address_proof_file = 'Address proof file is required';
      } else {
        const addressProofFileError = validateFile(
          addressProofFile,
          'Address proof file',
        );
        if (addressProofFileError)
          updatedErrors.address_proof_file = addressProofFileError;
      }

      // Update errors state with new validation results
      setErrors(updatedErrors);

      // If there are no errors, submit the form
      if (Object.keys(updatedErrors).length === 0) {
        setTrackSubmission('documentUpload', true);
        const formData = new FormData();
        formData.append('file_type', 'KYC');
        if (panFile) formData.append('files', panFile);
        if (addressProofFile) formData.append('files', addressProofFile);
        setShowLoader(true);
        props.onSubmit(formData);
      }
    };

    return (
      <Div>
        <form onSubmit={e => e.preventDefault()}>
          <Text>
            As a last step, verify yourself and you will be good to go!
          </Text>

          {/* GST Toggle */}
          <InputWrapper>
            <Wrapper
              className="d-flex align-items-center position-relative justify-content-start"
              style={{ gap: '15px', marginBottom: '15px' }}
            >
              <StyledCheckbox
                type="checkbox"
                checked={hasGst}
                onChange={e => {
                  setHasGst(e.target.checked);
                  if (!e.target.checked) {
                    setGstNumber('');
                    setErrors(prev => ({ ...prev, gst_number: '' }));
                  }
                }}
              />
              <div>
                <TermsText>I have a GST number</TermsText>
              </div>
            </Wrapper>

            {/* GST Number Input - Only show if hasGst is true */}
            {hasGst && (
              <Cover
                className="d-flex align-items-center"
                style={{ gap: '15px' }}
                hasError={!!errors.gst_number}
              >
                <img
                  style={{
                    width: '19px',
                    height: '19px',
                    display: 'flex',
                    alignSelf: 'center',
                  }}
                  src={solar}
                  alt=""
                />

                <StyledInput
                  type="text"
                  value={gstNumber}
                  onChange={e => {
                    setGstNumber(e.target.value);
                    handleGstNumberChange(e.target.value);
                  }}
                  placeholder="Enter GST number"
                  onBlur={e => {
                    handleGstNumberChange(e.target.value);
                  }}
                />
                {errors.gst_number && (
                  <Error className="ms-1">{errors.gst_number}</Error>
                )}
              </Cover>
            )}
          </InputWrapper>

          {/* PAN File Input */}
          <InputWrapper>
            <Cover
              className="d-flex justify-content-between align-items-center cursor-pointer"
              hasError={!!errors.PAN_file}
              onClick={e => {
                e.preventDefault();
                if (!identityFileSelected) {
                  identityFileInputRef.current?.click();
                }
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{ gap: '15px', width: '100%', overflow: 'hidden' }}
              >
                <img
                  style={{
                    width: '19px',
                    height: '19px',
                    display: 'flex',
                    alignSelf: 'center',
                    flexShrink: 0,
                  }}
                  src={
                    !identityFileSelected
                      ? `${AdvisorOnboardbaseUrl}/upload.svg`
                      : success
                  }
                  alt="not-found"
                />
                {identityFileSelected ? (
                  <PlaceHolder className="d-flex align-items-center">
                    {identityFileName}
                  </PlaceHolder>
                ) : (
                  <PlaceHolder className="d-flex align-items-center">
                    Upload PAN card image
                  </PlaceHolder>
                )}
              </div>
              <div
                className="d-flex align-items-center"
                style={{ gap: '10px' }}
                onClick={e => {
                  e.stopPropagation(); // Prevent triggering the parent onClick
                  if (identityFileSelected) {
                    if (identityFileInputRef.current) {
                      identityFileInputRef.current.value = '';
                    }
                    handlePanFileChange(null);
                  } else {
                    identityFileInputRef.current?.click();
                  }
                }}
              >
                <img
                  style={{
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignSelf: 'center',
                  }}
                  src={identityFileSelected ? Removefile : Add}
                  alt={identityFileSelected ? 'Remove file' : 'Add file'}
                  className="cursor-pointer"
                />
              </div>
              {errors.PAN_file && (
                <Error className="ms-1">{errors.PAN_file}</Error>
              )}
            </Cover>
            <input
              type="file"
              ref={identityFileInputRef}
              style={{ display: 'none' }}
              accept=".png,.jpg,.jpeg,.pdf"
              onChange={e => {
                const file = e.target.files?.[0];
                handlePanFileChange(file || null);
              }}
            />
          </InputWrapper>

          {/* Address Proof Type */}
          <Infotext>Proof of Address</Infotext>
          <div
            className="mt-2 d-flex flex-wrap position-relative"
            style={{ gap: '30px', rowGap: '20px' }}
          >
            {[
              'Aadhaar Card',
              'Passport',
              'Driving License',
              'Bank Statement',
            ].map(type => (
              <div
                key={type}
                className="d-flex align-items-center"
                style={{ gap: '15px' }}
              >
                <CustomRadioInput
                  type="radio"
                  name="address_proof_type"
                  value={type}
                  onChange={() => handleAddressProofTypeChange(type)}
                  checked={addressProofType === type}
                />
                <Textradio>{type}</Textradio>
              </div>
            ))}
            {errors.address_proof_type && (
              <Error className="ms-1 w-100">{errors.address_proof_type}</Error>
            )}
          </div>

          {/* Address Proof File Input */}
          <InputWrapper>
            <Cover
              className="d-flex justify-content-between align-items-center cursor-pointer"
              hasError={!!errors.address_proof_file}
              onClick={e => {
                e.preventDefault();
                if (!addressFileSelected) {
                  addressFileInputRef.current?.click();
                }
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{ gap: '15px', width: '100%', overflow: 'hidden' }}
              >
                <img
                  style={{
                    width: '19px',
                    height: '19px',
                    display: 'flex',
                    alignSelf: 'center',
                    flexShrink: 0,
                  }}
                  src={
                    !addressFileSelected
                      ? `${AdvisorOnboardbaseUrl}/upload.svg`
                      : success
                  }
                  alt="not-found"
                />
                {addressFileSelected ? (
                  <PlaceHolder className="d-flex align-items-center">
                    {addressFileName}
                  </PlaceHolder>
                ) : (
                  <PlaceHolder className="d-flex align-items-center">
                    Upload Address Proof
                  </PlaceHolder>
                )}
              </div>
              <div
                className="d-flex align-items-center"
                style={{ gap: '10px' }}
                onClick={e => {
                  e.stopPropagation(); // Prevent triggering the parent onClick
                  if (addressFileSelected) {
                    if (addressFileInputRef.current) {
                      addressFileInputRef.current.value = '';
                    }
                    handleAddressProofFileChange(null);
                  } else {
                    addressFileInputRef.current?.click();
                  }
                }}
              >
                <img
                  style={{
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    alignSelf: 'center',
                  }}
                  src={addressFileSelected ? Removefile : Add}
                  alt={addressFileSelected ? 'Remove file' : 'Add file'}
                  className="cursor-pointer"
                />
              </div>
              {errors.address_proof_file && (
                <Error className="ms-1">{errors.address_proof_file}</Error>
              )}
            </Cover>
            <input
              type="file"
              ref={addressFileInputRef}
              style={{ display: 'none' }}
              accept=".png,.jpg,.jpeg,.pdf"
              onChange={e => {
                const file = e.target.files?.[0];
                handleAddressProofFileChange(file || null);
              }}
            />
          </InputWrapper>

          {/* Continue Button */}
          <div className="d-flex justify-content-end">
            <Button onClick={handleSubmit} style={{ marginTop: '20px' }}>
              {showLoader ? (
                <Lottie
                  animationData={loader}
                  loop
                  style={{ height: '25px', width: '100%' }}
                />
              ) : (
                'continue'
              )}
            </Button>
          </div>
        </form>
      </Div>
    );
  }),
);

// Styled Components (same as before)
const Button = styled.button`
  height: 52px;
  background-color: #8276f4;
  padding: 15px 30px;
  border-radius: 30px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin-top: 20px;
  border: none;
  &:hover {
    background-color: #5c54ad;
  }
  ${media.medium} {
    position: absolute;
    bottom: 20px;
    right: 0;
  }
`;

const PlaceHolder = styled.h6`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  ${media.small} {
    font-weight: 500;
  }
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
`;
const Error = styled.div`
  color: #ff7b6a;
  font-weight: 400;
  font-size: 10px;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  white-space: nowrap;
  max-width: 100%;
  line-height: 1.2;
`;
const StyledInput = styled.input`
  padding: 0.5rem;
  font-size: 1rem;
  border: none;
  outline: none;
  flex: 1;
  background: transparent;
  width: 0;
  min-width: 0;
  &::placeholder {
    font-size: 14px;
    font-weight: 300;
    font-family: Nunito;
    letter-spacing: 0%;
    color: #838eae;
  }
`;
const CustomRadioInput = styled.input`
  &[type='radio'] {
    display: inline-block;
    width: 32px;
    height: 32px;
    border: 1.5px solid #8276f4;
    border-radius: 50%;
    background-color: white;
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    appearance: none;
    outline: none;
    ${media.large} {
      width: 21px; /* Adjust the size of the checkbox */
      height: 21px;
    }
    ${media.xlarge} {
      width: 21px; /* Adjust the size of the checkbox */
      height: 21px;
    }
  }

  &:checked {
    background-color: #8276f4;
    border-color: #8276f4;
  }
  &:checked::after {
    content: '';
    width: 5px; /* Width of the checkmark lines */
    height: 9.5px; /* Height of the checkmark lines */
    border: solid white;
    border-width: 0 1.5px 1.5px 0; /* Right and bottom borders */
    position: absolute;
    top: 45%;
    left: 31%;
    transform: translate(-50%, -50%) rotate(45deg); /* Rotate to form the "V" shape */
    transform-origin: bottom right;
  }
`;
const Cover = styled.div<{ hasError?: boolean }>`
  border: 2px solid ${props => (props.hasError ? '#FFC6BF' : '#C6C0FA')};
  height: 52px;
  width: 100%;
  border-radius: 10px;
  position: relative;
  background: #ffffff;
  padding: 16px 20px;
  cursor: pointer;
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 10px;
  margin-top: 30px;
`;

const Text = styled.div`
  width: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  ${media.small} {
    font-size: 14px;
    font-weight: 500;
  }
`;

const Textradio = styled.span`
  font-weight: 300;
  font-size: 16px;
`;

const Infotext = styled.h6`
  font-weight: 500;
  font-size: 16px;
  margin: 0;
  margin-top: 15px;
`;

const Div = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  ${media.small} {
    height: auto;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  justify-content: start;
  gap: 15px;
  margin-bottom: 15px;
`;

const StyledCheckbox = styled.input`
  &[type='checkbox'] {
    position: relative;
    width: 48px;
    height: 24px;
    appearance: none;
    background-color: #e4e6ec;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    outline: none;
    border: none;
  }

  &::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    top: 2px;
    left: 2px;
    transition: all 0.3s ease;
  }

  &:checked {
    background-color: #8276f4;
  }

  &:checked::before {
    transform: translateX(24px);
  }
`;

const TermsText = styled.span`
  font-weight: 300;
  font-size: 16px;
`;
